"use client";

import * as React from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "../lib/cn";
import { durations, easings } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useInterval } from "../hooks/useInterval";
import { useDocumentVisibility } from "../hooks/useDocumentVisibility";

/**
 * RotatingHints, a quiet single line of usage tips for wait states. Drop it
 * into the "thinking" / long-tool-run row of a chat surface so the user learns
 * what they can do while the assistant works, then let it disappear the moment
 * prose starts streaming.
 *
 * It is decorative on purpose (`aria-hidden`), so it never steals a screen
 * reader's attention from the real streamed answer. Mount it while you are
 * waiting; unmount it on the first prose token.
 *
 * Behavior:
 * - **Grace first.** Renders nothing until `graceMs` has elapsed, so a fast
 *   answer never flashes a tip. Keep this component mounted only during the
 *   wait, and it self-suppresses on quick turns.
 * - **Rotates.** After the grace, it shows one hint and advances to the next
 *   every `intervalMs`, cycling the list. The start index is random by default
 *   (so repeat visitors do not always open on hint 1); pass `startIndex` to pin
 *   it (tests do this for determinism).
 * - **House two-beat swap.** Each rotation slides on the library's signature
 *   `[0.16, 1, 0.3, 1]` curve with opacity ramping a beat after the transform,
 *   never simultaneous. Under `prefers-reduced-motion` it collapses to a calm
 *   opacity-only cross-fade with no transform.
 * - **No jank.** The incoming line takes the slot immediately (`popLayout`) and
 *   the outgoing line animates out on top, so a swap never shifts layout. The
 *   row is clipped to itself, and each tip is a single truncating line.
 * - **Pauses when hidden.** A backgrounded tab (`document.hidden`) stops the
 *   timer and resumes cleanly when the tab is visible again. All timers are
 *   cleaned up on unmount.
 *
 * Copy passes through verbatim (no formatting is applied). Author the tips in
 * product voice with no em-dashes, one line each; anything overlong truncates
 * with an ellipsis rather than wrapping.
 *
 * @example // while the assistant is thinking / tools are running
 *   {isWaiting ? <RotatingHints hints={ATLAS_TIPS} /> : null}
 */
export interface RotatingHintsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** The tips to cycle through, shown verbatim, one per rotation. Required. */
  hints: string[];
  /** Milliseconds each hint stays before the next one slides in. Default 10000. */
  intervalMs?: number;
  /**
   * Milliseconds to wait before showing the first hint. Nothing renders until
   * this elapses, so a fast answer never flashes a tip. Default 3000. Pass `0`
   * to show immediately.
   */
  graceMs?: number;
  /**
   * Index of the first hint to show. Omit for a random start (the default, so
   * users do not always open on hint 1); wrapped into range if out of bounds.
   * Mainly an injection seam for deterministic tests.
   */
  startIndex?: number;
}

/** Px the hint travels on the two-beat swap. Small: this is a quiet line. */
const SLIDE_DISTANCE = 8;

/**
 * Resolve the initial index once. A number wraps into range; otherwise pick a
 * random slot so repeat visitors do not always open on the same hint.
 */
function initialIndex(startIndex: number | undefined, length: number): number {
  if (length <= 0) return 0;
  if (startIndex != null) return ((startIndex % length) + length) % length;
  return Math.floor(Math.random() * length);
}

export const RotatingHints = React.forwardRef<HTMLDivElement, RotatingHintsProps>(
  ({ hints, intervalMs = 10000, graceMs = 3000, startIndex, className, ...rest }, ref) => {
    const reduced = useReducedMotion() !== false;
    const visible = useDocumentVisibility();

    // `index` grows without bound; the shown hint is `index` modulo the current
    // length, so the row stays valid even if `hints` changes underneath it, and
    // the growing key gives AnimatePresence a fresh child on every advance (even
    // when two hints share the same text).
    const [index, setIndex] = React.useState(() => initialIndex(startIndex, hints.length));

    // Hold back the first paint until the grace has elapsed.
    const [ready, setReady] = React.useState(graceMs <= 0);
    React.useEffect(() => {
      if (graceMs <= 0) {
        setReady(true);
        return;
      }
      setReady(false);
      const id = window.setTimeout(() => setReady(true), graceMs);
      return () => window.clearTimeout(id);
    }, [graceMs]);

    // Rotate only once shown, while the tab is visible, and only if there is
    // more than one hint to move between. `null` pauses the timer (and cleans
    // it up) the moment any of those stop holding.
    const rotating = ready && visible && hints.length > 1;
    useInterval(() => setIndex((i) => i + 1), rotating ? intervalMs : null);

    if (!ready || hints.length === 0) return null;

    const current = hints[((index % hints.length) + hints.length) % hints.length];

    // Slide-then-fade: the transform leads on the house curve, opacity ramps a
    // beat later. Reduced motion collapses to an opacity-only cross-fade.
    const fadeLead = Math.min(durations.surface * 0.2, 0.08);
    const enter = reduced
      ? { opacity: 1, transition: { duration: durations.hover, ease: easings.smoothOut } }
      : {
          opacity: 1,
          y: 0,
          transition: {
            y: { duration: durations.surface, ease: easings.smoothOut },
            opacity: { duration: durations.hover, ease: easings.smoothOut, delay: fadeLead },
          },
        };
    const leave = reduced
      ? { opacity: 0, transition: { duration: durations.hover, ease: easings.smoothOut } }
      : {
          opacity: 0,
          y: -SLIDE_DISTANCE,
          transition: {
            y: { duration: durations.surface, ease: easings.smoothOut },
            opacity: { duration: durations.hover, ease: easings.smoothOut },
          },
        };

    return (
      <div ref={ref} aria-hidden {...rest} className={cn("relative overflow-hidden", className)}>
        <AnimatePresence mode="popLayout">
          <motion.div
            key={index}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: SLIDE_DISTANCE }}
            animate={enter}
            exit={leave}
            className="truncate text-xs leading-relaxed text-muted-foreground"
          >
            {current}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  },
);
RotatingHints.displayName = "RotatingHints";
